import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import GoogleMapView from "@/components/GoogleMapView";
import AddressMap from "@/components/AddressMap";
import {
  ArrowRight, MapPin, Star, Clock, Ticket, Car, Train, Phone, Globe,
  Heart, ChevronLeft, ChevronRight, Map, Youtube, Users, Share2,
  Calendar, DollarSign, Instagram, Facebook, Sparkles, BusFront
} from "lucide-react";
import { toast } from "sonner";
import BackBar from "@/components/BackBar";

const ytEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

export default function AttractionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/attractions/${id}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    if (token) {
      axios.get(`${API}/favorites/ids`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setSaved(r.data.ids.includes(id))).catch(() => {});
    }
  }, [id, token]);

  if (loading) return <DashboardLayout title={t("جاري التحميل", "Loading")}><div className="glass-card rounded-2xl h-96 skeleton" /></DashboardLayout>;
  if (!data) return <DashboardLayout title={t("غير موجود", "Not found")}><div className="glass-card rounded-2xl p-12 text-center text-slate-400">{t("المكان غير موجود", "Place not found")}</div></DashboardLayout>;

  const images = data.images?.length ? data.images : [null];
  const yt = ytEmbed(data.video_url);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.address || `${data.name}, ${data.city}, ${data.state}`)}`;

  const toggleSave = async () => {
    if (!token) { navigate("/login"); return; }
    try {
      if (saved) {
        await axios.delete(`${API}/favorites/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        setSaved(false); toast.success(t("تم الحذف من المفضلة", "Removed"));
      } else {
        await axios.post(`${API}/favorites/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setSaved(true); toast.success(t("تم الحفظ", "Saved"));
      }
    } catch { toast.error(t("فشلت العملية", "Action failed")); }
  };

  const share = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: data.name, text: data.description?.slice(0, 100), url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url);
      toast.success(t("تم نسخ الرابط", "Link copied"));
    }
  };

  return (
    <DashboardLayout title={language === "ar" ? (data.name_ar || data.name) : data.name}>
      <div className="max-w-6xl space-y-6" data-testid="attraction-detail">
        <BackBar to="/discover" label_ar="اكتشف أمريكا" label_en="Discover America"
          crumbs={[{ to: "/dashboard", ar: "الرئيسية", en: "Home" }, { to: "/discover", ar: "اكتشف", en: "Discover" }, { ar: data.name_ar || data.name, en: data.name }]} />

        {/* Hero gallery */}
        <div className="glass-card rounded-2xl overflow-hidden purple-gold-glow">
          <div className="relative h-[420px] bg-slate-900">
            <ListingImage path={images[imgIdx]} alt={data.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/30 to-transparent" />
            {images.length > 1 && (
              <>
                <button onClick={() => setImgIdx((imgIdx - 1 + images.length) % images.length)} className="absolute top-1/2 right-3 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-colors"><ChevronRight size={20} /></button>
                <button onClick={() => setImgIdx((imgIdx + 1) % images.length)} className="absolute top-1/2 left-3 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-colors"><ChevronLeft size={20} /></button>
              </>
            )}
            <div className="absolute top-6 right-6 left-6 flex justify-end gap-2">
              <button onClick={toggleSave} className={`w-11 h-11 rounded-full backdrop-blur-md flex items-center justify-center transition-colors ${saved ? "bg-yellow-500 text-black" : "bg-black/60 text-white hover:bg-yellow-500 hover:text-black"}`} data-testid="detail-save-btn">
                <Heart size={18} fill={saved ? "currentColor" : "none"} />
              </button>
              <button onClick={share} className="w-11 h-11 rounded-full backdrop-blur-md bg-black/60 text-white flex items-center justify-center hover:bg-purple-500 transition-colors" data-testid="detail-share-btn">
                <Share2 size={18} />
              </button>
            </div>
            <div className="absolute bottom-6 right-6 left-6 flex items-end justify-between gap-3 flex-wrap">
              <div>
                {data.featured && <Badge className="bg-gradient-to-r from-purple-500 to-yellow-500 text-white border-0 mb-2">{t("مميز", "Featured")}</Badge>}
                <h1 className="text-3xl lg:text-4xl font-bold text-white drop-shadow-2xl">
                  {language === "ar" ? (data.name_ar || data.name) : data.name}
                </h1>
                <p className="text-slate-300 mt-1 flex items-center gap-1">
                  <MapPin size={14} className="text-yellow-500" /> {data.address}
                </p>
              </div>
              {data.rating !== undefined && data.rating !== null && (
                <div className="bg-yellow-500/90 text-black font-bold px-4 py-2 rounded-full flex items-center gap-1">
                  <Star size={16} fill="currentColor" /> {data.rating}
                </div>
              )}
            </div>
          </div>
          {images.length > 1 && (
            <div className="p-3 flex gap-2 overflow-x-auto">
              {images.map((p, i) => (
                <button key={i} onClick={() => setImgIdx(i)} className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 ${i === imgIdx ? "border-yellow-500" : "border-transparent"}`}>
                  <ListingImage path={p} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-2">{t("نبذة", "About")}</h3>
              <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{data.description}</p>
            </div>

            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <MapPin className="text-yellow-500" size={18} />
                {t("على الخريطة", "On the map")}
              </h3>
              {data.lat && data.lng ? (
                <GoogleMapView center={{ lat: data.lat, lng: data.lng }} zoom={14} markers={[{ lat: data.lat, lng: data.lng, title: data.name }]} height="h-72" />
              ) : (
                <AddressMap address={data.address || `${data.name}, ${data.city}, ${data.state}`} title={data.name} height="h-72" />
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {(data.estimated_cost || data.ticket_price) && (
                <div className="glass-card rounded-xl p-4 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-yellow-500/10 flex items-center justify-center"><DollarSign className="text-yellow-500" size={20} /></div>
                  <div>
                    <div className="text-xs text-slate-400">{t("التكلفة التقديرية", "Estimated cost")}</div>
                    <div className="text-white font-semibold text-sm">{data.estimated_cost || data.ticket_price}</div>
                  </div>
                </div>
              )}
              {data.opening_hours && (
                <div className="glass-card rounded-xl p-4 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-purple-500/10 flex items-center justify-center"><Clock className="text-purple-400" size={20} /></div>
                  <div>
                    <div className="text-xs text-slate-400">{t("أوقات العمل", "Opening hours")}</div>
                    <div className="text-white font-semibold text-sm">{data.opening_hours}</div>
                  </div>
                </div>
              )}
              {data.best_time_to_visit && (
                <div className="glass-card rounded-xl p-4 flex items-center gap-3 sm:col-span-2">
                  <div className="w-11 h-11 rounded-xl bg-yellow-500/10 flex items-center justify-center"><Calendar className="text-yellow-500" size={20} /></div>
                  <div>
                    <div className="text-xs text-slate-400">{t("أفضل وقت للزيارة", "Best time to visit")}</div>
                    <div className="text-white font-semibold text-sm">{data.best_time_to_visit}</div>
                  </div>
                </div>
              )}
              <div className="glass-card rounded-xl p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-yellow-500/10 flex items-center justify-center"><Users className="text-yellow-500" size={20} /></div>
                <div>
                  <div className="text-xs text-slate-400">{t("مناسب للعائلات", "Family friendly")}</div>
                  <div className="text-white font-semibold">{data.family_friendly ? t("نعم", "Yes") : t("لا", "No")}</div>
                </div>
              </div>
              <div className="glass-card rounded-xl p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-yellow-500/10 flex items-center justify-center"><Car className="text-yellow-500" size={20} /></div>
                <div>
                  <div className="text-xs text-slate-400">{t("موقف سيارات", "Parking")}</div>
                  <div className="text-white font-semibold">{data.parking_available ? t("متاح", "Available") : t("غير متاح", "Not available")}</div>
                </div>
              </div>
            </div>

            {data.nearby_transit && (
              <div className="glass-card rounded-2xl p-5 flex gap-3">
                <Train className="text-yellow-500 flex-shrink-0" size={20} />
                <div>
                  <h4 className="font-bold text-white mb-1">{t("المواصلات القريبة", "Nearby transit")}</h4>
                  <p className="text-slate-300 text-sm">{data.nearby_transit}</p>
                </div>
              </div>
            )}

            {data.transportation_tips && (
              <div className="glass-card rounded-2xl p-5 flex gap-3 border-purple-500/30">
                <BusFront className="text-purple-400 flex-shrink-0" size={20} />
                <div>
                  <h4 className="font-bold text-white mb-1">{t("نصائح التنقل", "Transportation tips")}</h4>
                  <p className="text-slate-300 text-sm">{data.transportation_tips}</p>
                </div>
              </div>
            )}

            {data.arabic_tip && (
              <div className="glass-card rounded-2xl p-5 flex gap-3 bg-luxury">
                <Sparkles className="text-yellow-400 flex-shrink-0" size={20} />
                <div>
                  <h4 className="font-bold text-luxury mb-1">{t("نصيحة عربية", "Arabic tip")}</h4>
                  <p className="text-slate-200 text-sm">{data.arabic_tip}</p>
                </div>
              </div>
            )}

            {yt && (
              <div className="glass-card rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-800 flex items-center gap-2">
                  <Youtube className="text-red-500" size={20} />
                  <h4 className="font-bold text-white">{t("فيديو تعريفي", "Tour video")}</h4>
                </div>
                <div className="aspect-video">
                  <iframe src={yt} title="YouTube" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="glass-card rounded-2xl p-5 space-y-3">
              <a href={mapsUrl} target="_blank" rel="noreferrer" className="block">
                <Button className="w-full btn-primary" data-testid="open-maps-btn">
                  <Map className="ml-2" size={18} /> {t("افتح في خرائط Google", "Open in Google Maps")}
                </Button>
              </a>
              {data.distance_miles !== undefined && (
                <div className="bg-luxury border border-yellow-500/30 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-luxury">{data.distance_miles} mi</div>
                  <div className="text-xs text-slate-400">{t("المسافة من موقعك", "Distance from your location")}</div>
                </div>
              )}
              {data.phone && (
                <a href={`tel:${data.phone}`}>
                  <Button variant="outline" className="w-full border-slate-700 text-white">
                    <Phone className="ml-2" size={18} /> {data.phone}
                  </Button>
                </a>
              )}
              {data.website && (
                <a href={data.website} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="w-full border-slate-700 text-white">
                    <Globe className="ml-2" size={18} /> {t("الموقع الرسمي", "Website")}
                  </Button>
                </a>
              )}
              <div className="flex gap-2">
                {data.social_instagram && (
                  <a href={data.social_instagram} target="_blank" rel="noreferrer" className="flex-1">
                    <Button variant="outline" className="w-full border-slate-700 text-white hover:text-pink-400 hover:border-pink-400/40">
                      <Instagram size={18} />
                    </Button>
                  </a>
                )}
                {data.social_facebook && (
                  <a href={data.social_facebook} target="_blank" rel="noreferrer" className="flex-1">
                    <Button variant="outline" className="w-full border-slate-700 text-white hover:text-blue-400 hover:border-blue-400/40">
                      <Facebook size={18} />
                    </Button>
                  </a>
                )}
              </div>
            </div>

            <div className="glass-card rounded-2xl p-5">
              <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                <Heart size={18} className="text-yellow-500" /> {t("نصائح للزيارة", "Visit tips")}
              </h4>
              <ul className="text-sm text-slate-400 space-y-1 list-disc pr-5">
                <li>{t("احجز التذاكر مسبقاً للأماكن الشهيرة", "Book tickets ahead for popular spots")}</li>
                <li>{t("استخدم المترو/الباص لتجنب الازدحام", "Use metro/bus to skip traffic")}</li>
                <li>{t("الذروة أيام العطل الأسبوعية", "Weekends get crowded")}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

